import { ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { usePermissionsBoundary } from '../../../../core/permissions/permissions-boundary.hook';

// This component wraps the given children in a Link component if the following consitions are satisfied:
// 1. The link prop is defined
// 2. The user has the required permissions to access the link or...
// 3. The user has the required UI features to access the link

export function OptionalLink({
  children,
  link,
  ...permissionsBoundaryProps
}: { link?: string; children: ReactNode } & ComponentProps<typeof PermissionBoundary>) {
  if (permissionsBoundaryProps.availableUiFeatures || permissionsBoundaryProps.permissions) {
    return (
      <OptionalLinkWithPermissionsCheck link={link} {...permissionsBoundaryProps}>
        {children}
      </OptionalLinkWithPermissionsCheck>
    );
  }

  return <OptionalLinkBase link={link}>{children}</OptionalLinkBase>;
}

function OptionalLinkWithPermissionsCheck({
  children,
  link,
  availableUiFeatures,
  permissions,
  onlyRequireSomePermissions
}: ComponentProps<typeof OptionalLink>) {
  const { isLoading, isRejected } = usePermissionsBoundary({
    availableUiFeatures,
    onlyRequireSomePermissions,
    permissions
  });

  if (isLoading || isRejected) {
    return <>{children}</>;
  }

  return <OptionalLinkBase link={link}>{children}</OptionalLinkBase>;
}

function OptionalLinkBase({ children, link }: { link?: string; children: ReactNode }) {
  return link ? <Link to={link}>{children}</Link> : <>{children}</>;
}
